import { INotificationData } from './notificationData';

export async function displayNotification({
  title,
  body,
  imageUrl,
  data,
}: {
  title: string;
  body: string;
  imageUrl?: string;
  data?: INotificationData;
}) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    console.warn('Notifications: service workers not supported in this environment');
    return;
  }

  const linkingPath = data?.deeplinkingUrl?.toString()?.replace('ninjakisan://', '');
  const notificationOptions: NotificationOptions = {
    body,
    icon: './favicon.ico',
    data: {
      path: linkingPath,
      id: data?.campaignId,
      ...(imageUrl && {
        largeIcon: { uri: imageUrl },
        style: { type: 'BIGPICTURE', picture: imageUrl },
      }),
    },
    ...(imageUrl && { image: imageUrl }),
  };

  try {
    const registration = await getMessagingServiceWorkerRegistration();
    if (!registration) {
      console.warn('Notifications: firebase messaging service worker not ready');
      return;
    }

    await registration.showNotification(title, notificationOptions);
  } catch (error) {
    console.error('Notifications: failed to display notification', error);
  }
}

const getMessagingServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  const matchesMessagingSw = (registration?: ServiceWorkerRegistration | null) => {
    if (!registration) return false;
    const workers = [registration.active, registration.waiting, registration.installing];
    return workers.some((worker) => worker?.scriptURL.includes('firebase-messaging-sw.js'));
  };

  try {
    const readyRegistration = await navigator.serviceWorker.ready;
    if (matchesMessagingSw(readyRegistration)) {
      return readyRegistration;
    }
  } catch (error) {
    console.warn('Notifications: awaiting service worker ready failed', error);
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const matchingRegistration = registrations.find((registration) => matchesMessagingSw(registration));

    if (matchingRegistration) {
      return matchingRegistration;
    }
  } catch (error) {
    console.warn('Notifications: failed to fetch service worker registrations', error);
  }

  try {
    const specificRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (specificRegistration && matchesMessagingSw(specificRegistration)) {
      return specificRegistration;
    }
  } catch (error) {
    console.warn('Notifications: failed to fetch specific messaging service worker', error);
  }

  if (Notification.permission === 'granted') {
    try {
      const newlyRegistered = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      if (matchesMessagingSw(newlyRegistered)) {
        return newlyRegistered;
      }
    } catch (error) {
      console.warn('Notifications: failed to register firebase messaging service worker on demand', error);
    }
  }

  return null;
};
